import service from '@/api/user';
import {loading, alert, handleError, destroyLocal, storeLocal, getLocal} from '@/state/generators/helpers';

export const state = {
    loading: false,
    loadingSearch: false,
    searchList: [],
    searchFilters: {},
    searchPager: {},
    loadingPositions: false,
    positions: [],
    positionPivots: [],
    loadingSkills: false,
    skills: [],
    skillPivots: [],
    authenticated: getLocal('user') || {},
    service: service('users'),
    implementers: [],
    potentialSubordinates: [],
    profile: {},
    loadingProfile: false,
    checkedSubordinates: {},
    loadingCheckSubordinate: false,
    checkedSuperiors: {},
    loadingCheckSuperior: false,
    loadingTasks: false,
    tasks: [],
    calendarLoading: false,
    calendar: [],
    subordinatesWithDetails: [],
    permissionLoading: false,
    permissionBasicsLoading: false,
    permissionRoleGroupsLoading: false,
    permissionRolesLoading: false,
    permissionTree: [],
    roleGroups: [],
    basics: {}
}

export const getters = {
    authenticated: (state) => {
        return JSON.parse(JSON.stringify(state.authenticated))
    },

    searchList: (state) => {
        return JSON.parse(JSON.stringify(state.searchList))
    },

    loadingSearch: (state) => {
        return !!state.loadingSearch
    },

    loadingPositions: (state) => {
        return !!state.loadingPositions
    },

    positions: (state) => {
        return JSON.parse(JSON.stringify(state.positions))
    },

    positionPivots: (state) => {
        return JSON.parse(JSON.stringify(state.positionPivots))
    },

    loadingSkills: (state) => {
        return !!state.loadingSkills
    },

    skills: (state) => {
        return JSON.parse(JSON.stringify(state.skills))
    },

    skillPivots: (state) => {
        return JSON.parse(JSON.stringify(state.skillPivots))
    },

    searchEnd: (state) => {
        return state.searchPager?.total === state.searchList?.length
    },

    loadingProfile: (state) => {
        return !!state.loadingProfile;
    },

    profile: (state) => {
        return JSON.parse(JSON.stringify(state.profile))
    },

    subordinates: (state) => {
        if (! state.profile.subordinates) {
            return [];
        }

        return JSON.parse(JSON.stringify(state.profile.subordinates));
    },

    firstLetter: (state) => {
        return state.authenticated.id ? state.authenticated.firstname[0] : ''
    },

    implementers: (state) => {
        return state.implementers
    },

    potentialSubordinates: (state) => {
        return state.potentialSubordinates
    },

    loadingCheckSubordinate: (state) => {
        return state.loadingCheckSubordinate;
    },

    loadingCheckSuperior: (state) => {
        return state.loadingCheckSuperior;
    },

    loadingTasks: (state) => {
        return !!state.loadingTasks;
    },

    tasks: (state) => {
        return JSON.parse(JSON.stringify(state.tasks))
    },

    calendarLoading: (state) => {
        return !!state.calendarLoading
    },

    calendar: (state) => {
        return JSON.parse(JSON.stringify(state.calendar))
    },

    subordinatesWithDetails: (state) => {
        return JSON.parse(JSON.stringify(state.subordinatesWithDetails));
    },

    permissionLoading: (state) => {
        return !!state.permissionLoading;
    },

    permissionBasicsLoading: (state) => {
        return !!state.permissionBasicsLoading;
    },

    permissionRoleGroupsLoading: (state) => {
        return !!state.permissionRoleGroupsLoading;
    },

    permissionRolesLoading: (state) => {
        return !!state.permissionRolesLoading;
    },

    roleGroups: (state) => {
        return JSON.parse(JSON.stringify(state.roleGroups));
    },

    permissionTree: (state) => {
        return JSON.parse(JSON.stringify(state.permissionTree));
    },

    basics: (state) => {
        return Object.assign({}, state.basics);
    }
}

export const mutations = {
    'SET_LOADING': function (state, boolean = true) {
        state.loading = boolean
    },

    'SET_LOADING_PROFILE': function (state, boolean = true) {
        state.loadingProfile = boolean;
    },

    'SET_AUTHENTICATED': function (state, user) {
        state.authenticated = user;
        user ? storeLocal('user', user) : destroyLocal('user');
    },

    'SET_PROFILE': function (state, user) {
        state.profile = user
    },

    'SET_IMPLEMENTERS': function (state, list = []) {
        state.implementers = list
    },

    'SET_POTENTIAL_SUBORDINATES': function (state, list = []) {
        state.potentialSubordinates = list
    },

    'SET_LOADING_CHECK_SUBORDINATE': function (state, loading) {
        state.loadingCheckSubordinate = loading
    },

    'SET_LOADING_CHECK_SUPERIOR': function (state, loading) {
        state.loadingCheckSuperior = loading
    },

    'SET_SEARCH_LIST': function (state, list = []) {
        state.searchList = list
    },

    'ADD_TO_SEARCH_LIST': function (state, list = []) {
        state.searchList = [...state.searchList, ...list]
    },

    'SET_LOADING_SEARCH': function (state, loading) {
        state.loadingSearch = loading
    },

    'SET_CHECK_SUBORDINATE': function (state, check) {
        state.checkedSubordinates = Object.assign(state.checkedSubordinates, check);
    },

    'SET_CHECK_SUPERIOR': function (state, check) {
        state.checkedSuperiors = Object.assign(state.checkedSuperiors, check);
    },

    'SET_SEARCH_FILTERS': function (state, filters) {
        state.searchFilters = filters;
    },

    'SET_SEARCH_PAGER': function (state, pager) {
        state.searchPager = pager;
    },

    'SET_POSITIONS': function (state, list = []) {
        state.positions = list;
    },

    'SET_POSITION_PIVOTS': function (state, list = []) {
        state.positionPivots = list;
    },

    'SET_LOADING_POSITIONS': function (state, loading) {
        state.loadingPositions = loading
    },

    'SET_SKILLS': function (state, list = []) {
        state.skills = list;
    },

    'SET_SKILL_PIVOTS': function (state, list = []) {
        state.skillPivots = list;
    },

    'SET_LOADING_SKILLS': function (state, loading) {
        state.loadingSkills = loading
    },

    'SET_TASKS': function (state, list = []) {
        state.tasks = list;
    },

    'SET_LOADING_TASKS': function (state, loading) {
        state.loadingTasks = loading
    },

    'SET_CALENDAR': function (state, list = []) {
        state.calendar = list;
    },

    'SET_LOADING_CALENDAR': function (state, loading) {
        state.calendarLoading = loading
    },

    'UPDATE_TASK_STATUS': function (state, {id, status}) {
        const index = state.tasks.findIndex(listItem => listItem.id === id);

        if (index !== -1) {
            state.tasks[index].status = status
            state.tasks = JSON.parse(JSON.stringify(state.tasks))
        }
    },

    'REMOVE_TASK_FROM_LIST': function (state, id) {
        const index = state.tasks.findIndex(listItem => listItem.id === id);

        if (index !== -1) {
            state.tasks.splice(index, 1);
            state.tasks = JSON.parse(JSON.stringify(state.tasks));
        }
    },

    'UPDATE_TASK_ASSIGNED': function (state, {id, user}) {
        const index = state.tasks.findIndex(listItem => listItem.id === id);

        if (index !== -1) {
            state.tasks[index].assigned_id = user.id;
            state.tasks[index].assigned_type = 'App\\Models\\User';
            state.tasks[index].assigned = user;
            state.tasks = JSON.parse(JSON.stringify(state.tasks))
        }
    },

    'SET_SUBORDINATES_WITH_DETAILS': function (state, list = []) {
        state.subordinatesWithDetails = list;
    },

    'SET_PERMISSION_TREE': function (state, list = []) {
        state.permissionTree = list;
    },

    'SET_PERMISSION_LOADING': function (state, loading) {
        state.permissionLoading = loading;
    },

    'SET_PERMISSION_BASICS_LOADING': function (state, loading) {
        state.permissionBasicsLoading = loading;
    },

    'SET_PERMISSION_ROLE_GROUPS_LOADING': function (state, loading) {
        state.permissionRoleGroupsLoading = loading;
    },

    'SET_PERMISSION_ROLES_LOADING': function (state, loading) {
        state.permissionRolesLoading = loading;
    },

    'SET_ROLE_GROUPS': function (state, list) {
        state.roleGroups = list;
    },

    'SET_BASICS': function (state, data) {
        state.basics = data;
    },
}

export const actions = {
    authenticated: function ({commit, state, dispatch}) {
        loading(commit)

        return state.service.fetchAuthenticated().then(data => {
            commit('SET_AUTHENTICATED', data.data)
            dispatch('notification/fetch', {}, {root: true})
            loading(commit, false)
            return data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    profile: function ({commit, state}, id) {
        loading(commit);
        commit('SET_LOADING_PROFILE');

        return state.service.fetchProfile(id).then(data => {
            commit('SET_PROFILE', data.data);
            loading(commit, false);
            commit('SET_LOADING_PROFILE', false);
            return data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    setAuthenticated: function ({commit}, user) {
        commit('SET_AUTHENTICATED', user)
    },

    clearProfile: function ({commit}) {
        commit('SET_PROFILE', {})
    },

    changePassword: function ({commit, state}, passwords) {
        loading(commit)

        const id = passwords.id

        delete passwords.id

        return state.service.changePassword(id, passwords).then(data => {
            loading(commit, false)
            commit('SET_ERRORS', {})
            alert(commit, this.$t('users.password_changed').ucFirst())
            return data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    roles: function ({commit, state}, payload) {
        loading(commit)

        const id = payload.id
        delete payload.id

        return state.service.syncRoles(id, payload).then(response => {
            loading(commit, false)
            alert(commit, this.$t('request.save_is_successful'))
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    roleGroups: function ({commit, state}, payload) {
        loading(commit)

        const id = payload.id
        delete payload.id

        return state.service.syncRoleGroups(id, payload).then(response => {
            loading(commit, false)
            alert(commit, this.$t('request.save_is_successful'))
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    implementers: function ({commit, state}, filters) {
        loading(commit)

        return state.service.fetchImplementers(filters).then(response => {
            commit('SET_IMPLEMENTERS', response.data)
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    potentialSuperiors: function ({commit, state}, payload = null) {
        loading(commit)

        return state.service.fetchPotentialSuperiors(payload?.id || state.authenticated?.id).then(response => {
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    potentialSubordinates: function ({commit, state}, payload = null) {
        loading(commit)

        return state.service.fetchPotentialSubordinates(payload?.id || state.authenticated?.id).then(response => {
            commit('SET_POTENTIAL_SUBORDINATES', response.data)
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    subordinates: function ({commit, state}, payload = null) {
        loading(commit);

        return state.service.fetchSubordinates(payload?.id || state.authenticated?.id).then(response => {
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    subordinatesWithDetails: function ({commit, state /*getters*/}, payload = null) {
        loading(commit);

        /*if (state.subordinatesWithDetails?.length) {
            return getters.subordinatesWithDetails;
        }*/

        return state.service.fetchSubordinatesWithDetails(payload?.id || state.authenticated?.id, {recursive: payload.recursive}).then(response => {
            loading(commit, false);
            commit('SET_SUBORDINATES_WITH_DETAILS', response.data);
            return response.data;
        }).catch((e) => {
            handleError(e, commit)
        });
    },

    superior: function ({commit, state}, payload = null) {
        loading(commit)

        return state.service.fetchSuperior(payload?.id || state.authenticated?.id).then(response => {
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    syncSubordinates: function ({commit, state}, {id, data}) {
        loading(commit)

        return state.service.syncSubordinates(id || state.authenticated?.id, data).then(response => {
            alert(commit, this.$t('request.save_is_successful'))
            loading(commit, false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    checkSubordinate: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_CHECK_SUBORDINATE', true)

        if (Object.keys(state.checkedSubordinates).includes(id)) {
            return state.checkedSubordinates[id]
        }

        return state.service.checkSubordinate(id).then(response => {
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_CHECK_SUBORDINATE', false)
            commit('SET_CHECK_SUBORDINATE', response.data)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    checkSuperior: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_CHECK_SUPERIOR', true)

        if (Object.keys(state.checkedSuperiors).includes(id)) {
            return state.checkedSuperiors[id]
        }

        return state.service.checkSuperior(id).then(response => {
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_CHECK_SUPERIOR', false)
            commit('SET_CHECK_SUPERIOR', response.data)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    search: function ({commit, state}, filters) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_SEARCH', true)

        const data = Object.assign({page: 1}, filters)

        commit('SET_SEARCH_FILTERS', data)

        return state.service.search(data).then(response => {
            commit('SET_SEARCH_LIST', response.data)
            commit('SET_SEARCH_PAGER', response.meta)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_SEARCH', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
            commit('SET_SEARCH_FILTERS', {})
        })
    },

    moreSearch: function ({commit, state}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_SEARCH', true)

        const data = Object.assign({}, state.searchFilters, {page: (state.searchPager?.current_page || 1) + 1})

        return state.service.search(data).then(response => {
            commit('ADD_TO_SEARCH_LIST', response.data)
            commit('SET_SEARCH_PAGER', response.meta)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_SEARCH', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    clearSearch: function ({commit}) {
        commit('SET_SEARCH_FILTERS', {})
        commit('SET_SEARCH_LIST', [])
        commit('SET_SEARCH_PAGER', {})
    },

    positions: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_POSITIONS', true)


        return state.service.fetchPositions(id).then(response => {
            commit('SET_POSITIONS', response.data)
            commit('SET_POSITION_PIVOTS', response.pivots)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_POSITIONS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    syncPositions: function ({commit, state}, {id, pivots}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_POSITIONS', true)

        return state.service.updatePositions(id, {pivots: pivots}).then(response => {
            commit('SET_POSITIONS', response.data)
            commit('SET_POSITION_PIVOTS', response.pivots)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_POSITIONS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    skills: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_SKILLS', true)


        return state.service.fetchSkills(id).then(response => {
            commit('SET_SKILLS', response.data)
            commit('SET_SKILL_PIVOTS', response.pivots)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_SKILLS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    syncSkills: function ({commit, state}, {id, pivots}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_SKILLS', true)

        return state.service.updateSkills(id, {pivots: pivots}).then(response => {
            commit('SET_SKILLS', response.data)
            commit('SET_SKILL_PIVOTS', response.pivots)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_SKILLS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    tasks: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_TASKS', true)

        return state.service.fetchTasks(id, {with: ['interaction', 'projectStep'], sorting: { column: 'deadline', desc: false }} ).then(response => {
            commit('SET_TASKS', response.data)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_TASKS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    clearTasks: function ({commit}) {
        commit('SET_TASKS', []);
    },

    closedTasks: function ({commit, state}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_TASKS', true)

        return state.service.fetchClosedTasks(id, { sorting: { column: 'closed_at', desc: true } }).then(response => {
            commit('SET_TASKS', response.data)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_TASKS', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    removeTaskFromList: function ({commit}, id) {
        commit('REMOVE_TASK_FROM_LIST', id);
    },

    updateTaskStatus: function ({commit}, {id, status}) {
        commit('UPDATE_TASK_STATUS', {id: id, status: status});
    },

    updateTaskAssigned: function ({commit}, payload) {
        commit('UPDATE_TASK_ASSIGNED', payload);
    },

    calendar: function ({commit}, {id, filters}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_LOADING_CALENDAR', true)

        return state.service.fetchCalendar(id, filters).then(response => {
            commit('SET_CALENDAR', response.data)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_LOADING_CALENDAR', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    fetchRoles: function ({commit}, id) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_PERMISSION_LOADING', true)

        return state.service.fetchRoles(id).then(response => {
            commit('SET_PERMISSION_TREE', response.data)
            commit('SET_ROLE_GROUPS', response.role_groups)
            commit('SET_BASICS', response.basics)
            commit('app/SET_LOADING', false, {root: true})
            commit('SET_PERMISSION_LOADING', false)
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    saveRoles: function ({commit}, {id, roles}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_PERMISSION_ROLES_LOADING', true)

        return state.service.saveRoles(id, roles).then(response => {
            commit('SET_PERMISSION_TREE', response.data)
            commit('app/SET_LOADING', false, {root: true})
            alert(commit, this.$t('request.save_is_successful'))
            commit('SET_PERMISSION_ROLES_LOADING', false)
            return response.data;
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    saveRoleGroups: function ({commit}, {id, data}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_PERMISSION_ROLE_GROUPS_LOADING', true);

        return state.service.saveRoleGroups(id, data).then(response => {
            commit('SET_ROLE_GROUPS', response.data);
            commit('app/SET_LOADING', false, {root: true})
            alert(commit, this.$t('request.save_is_successful'))
            commit('SET_PERMISSION_ROLE_GROUPS_LOADING', false)
            return response.data;
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    saveBasics: function ({commit}, {id, data}) {
        commit('app/SET_LOADING', true, {root: true})
        commit('SET_PERMISSION_BASICS_LOADING', true)

        return state.service.saveBasics(id, data).then(response => {
            commit('SET_BASICS', response.data);
            commit('app/SET_LOADING', false, {root: true})
            alert(commit, this.$t('request.save_is_successful'))
            commit('SET_PERMISSION_BASICS_LOADING', false)
            return response.data;
        }).catch((e) => {
            handleError(e, commit)
        })
    },
}
